import { DeleteIcon } from "@chakra-ui/icons";
import {
  Button,
  ButtonGroup,
  Divider,
  Flex,
  HStack,
  Image,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Text,
  Textarea,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useData } from "../../Contexts/DataContext";
import { uploadFile, deleteFilesFromUrls } from "../../Utils/fileStorageUtils";
import { useAuth } from "../../Contexts/AuthContext";

const AddEditIdeaModal = ({ isOpen, onClose, data = null }) => {
  const {
    addDocument,
    updateDocument,
    deleteDocument,
    selectedClient,
    requirements = [],
    selectedSow,
  } = useData();

  const toast = useToast();
  const isEditing = !!data?.id;
  const { currentUser } = useAuth();
  const [note, setNote] = useState("");
  const [relatesTo, setRelatesTo] = useState("");
  const [selectedRelatesOption, setSelectedRelatesOption] = useState(""); // for dropdown
  const [screenshots, setScreenshots] = useState([]);
  const [loading, setLoading] = useState(false);

  const ideaOptions = [
    // ...(selectedSow?.deliverables || []).map((d) => `Deliverable: ${d.name}`),
    ...requirements.map((r) => `${r.title}`),
    "Something else",
  ];

  useEffect(() => {
    if (isOpen) {
      setNote(data?.note || "");
      setRelatesTo(data?.relatesTo || "");
      setScreenshots(data?.screenshots || []);
      setSelectedRelatesOption(
        ideaOptions.includes(data?.relatesTo) ? data?.relatesTo : "Relates to"
      );
    }
  }, [data, isOpen]);

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setScreenshots((prev) => [...prev, ...files]);
  };

  const handleFileRemove = (index) => {
    setScreenshots((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    const finalRelatesTo =
      selectedRelatesOption === "Something else"
        ? relatesTo?.trim()
        : selectedRelatesOption?.trim();

    if (!note.trim() || !finalRelatesTo) {
      toast({
        title: "Please complete the note and relation field",
        status: "warning",
        duration: 2000,
        isClosable: true,
      });
      return;
    }

    setLoading(true);
    try {
      const uploadedScreenshots = await Promise.all(
        screenshots.map(async (file) => {
          if (file instanceof File) {
            return await uploadFile(
              file,
              `screenshots/ideas/${data?.id || "temp"}`
            );
          }
          return file;
        })
      );

      const ideaData = {
        note,
        relatesTo: finalRelatesTo,
        screenshots: uploadedScreenshots,
        updatedAt: new Date().toISOString(),
        submittedBy: currentUser?.displayName,
      };

      if (isEditing) {
        await updateDocument("ideas", data.id, ideaData);
        toast({ title: "Idea updated", status: "success", duration: 1500 });
      } else {
        await addDocument("ideas", {
          ...ideaData,
          clientId: selectedClient?.id,
          createdAt: new Date().toISOString(),
        });
        toast({ title: "Idea added", status: "success", duration: 1500 });
      }

      onClose();
    } catch (error) {
      console.error("Error saving idea:", error);
      toast({
        title: "Failed to save idea",
        status: "error",
        duration: 2000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!data?.id) return;
    setLoading(true);

    try {
      if (data?.screenshots?.length > 0) {
        await deleteFilesFromUrls(data.screenshots);
      }

      await deleteDocument("ideas", data.id);
      toast({ title: "Idea deleted", status: "info", duration: 1500 });
    } catch (err) {
      toast({
        title: "Failed to delete idea",
        status: "error",
        duration: 2000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
      onClose();
    }
  };

  const openInNewTab = (url) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const isImageUrl = (value) =>
    typeof value === "string" && value.match(/\.(jpeg|jpg|png|gif|webp)$/i);

  const isFormValid =
    note.trim() &&
    selectedRelatesOption &&
    selectedRelatesOption !== "Relates to" &&
    (selectedRelatesOption === "Something else" ? relatesTo.trim() : true);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          bg="gray.700"
          color="white"
        >
          {isEditing ? "Edit Idea" : "Add New Idea"}
          <ModalCloseButton color="white" />
        </ModalHeader>

        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Select
              placeholder="Relates to..."
              value={selectedRelatesOption}
              onChange={(e) => {
                setSelectedRelatesOption(e.target.value);
                if (e.target.value !== "Something else") {
                  setRelatesTo(""); // clear manual entry if they switch
                }
              }}
            >
              {ideaOptions.map((opt, i) => (
                <option key={i} value={opt}>
                  {opt}
                </option>
              ))}
            </Select>

            {selectedRelatesOption === "Something else" && (
              <Input
                placeholder="Enter custom relation"
                value={relatesTo}
                onChange={(e) => setRelatesTo(e.target.value)}
              />
            )}

            <Textarea
              placeholder="What are you thinking?"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />

            <Input type="file" multiple onChange={handleFileChange} />

            {screenshots.length > 0 && (
              <VStack align="start" spacing={2} w="100%">
                <Text fontSize="sm" fontWeight="semibold" mt={2}>
                  Attachments:
                </Text>
                {screenshots.map((file, index) => {
                  const fileName = file?.name || file;
                  const fileUrl =
                    typeof file === "string" ? file : URL.createObjectURL(file);

                  return (
                    <HStack
                      key={index}
                      justifyContent="space-between"
                      w="100%"
                      align="center"
                      p={2}
                      borderWidth="1px"
                      borderRadius="md"
                    >
                      <Text
                        fontSize="sm"
                        cursor="pointer"
                        onClick={() => openInNewTab(fileUrl)}
                        textDecor="underline"
                        color="blue.600"
                        noOfLines={1}
                      >
                        {fileName}
                      </Text>

                      {isImageUrl(fileUrl) && (
                        <Image
                          src={fileUrl}
                          alt="Preview"
                          boxSize="40px"
                          objectFit="cover"
                          borderRadius="md"
                          onClick={() => openInNewTab(fileUrl)}
                          cursor="pointer"
                        />
                      )}

                      <DeleteIcon
                        onClick={() => handleFileRemove(index)}
                        cursor="pointer"
                      />
                    </HStack>
                  );
                })}
              </VStack>
            )}
          </VStack>
        </ModalBody>

        <Divider />
        <ModalFooter>
          <Flex justifyContent="space-between" w="100%">
            {isEditing && (
              <Button
                onClick={handleDelete}
                colorScheme="red"
                variant="outline"
                isLoading={loading}
              >
                Delete
              </Button>
            )}
            <ButtonGroup ml="auto">
              <Button
                onClick={handleSubmit}
                colorScheme="blue"
                isLoading={loading}
                isDisabled={!isFormValid}
              >
                {isEditing ? "Update" : "Add"}
              </Button>

              <Button onClick={onClose} variant="ghost" colorScheme="gray">
                Cancel
              </Button>
            </ButtonGroup>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AddEditIdeaModal;
